import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, CheckIcon, ShieldCheckIcon, TruckIcon } from 'lucide-react';
import { getProduct, products } from '../data/products';
import { getCategory } from '../data/categories';
import { Button } from '../components/ui/Button';
import { ProductCard } from '../components/shop/ProductCard';
import { CtaBand } from '../components/ui/CtaBand';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { useBrand } from '../contexts/BrandContext';
export function ProductDetail() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const product = slug ? getProduct(slug) : undefined;
  const {
    brand,
    text,
    productImage
  } = useBrand();
  useDocumentMeta({
    title: product ? `${product.name} — ${brand.name}` : `Shop — ${brand.name}`,
    description: product ? `${product.tagline} ${product.price}${product.cadence} — ${product.priceNote}.` : 'Doctor-prescribed treatments with the price in the open.',
    image: product ? productImage(product.slug, product.image) : brand.images.hero
  });
  if (!product) {
    return <main className="bg-cream" data-id="element-493" data-mp="3oo2cjn3up0el">
        <div className="mx-auto max-w-shell px-6 py-32 md:px-8" data-id="element-494" data-mp="3opdb98ifvhyu">
          <h1 className="max-w-2xl font-serif text-4xl font-semibold leading-tight text-ink md:text-5xl" data-id="element-495" data-mp="3ax5idrua0b5f">
            That page has moved on.
          </h1>
          <Button to="/shop" className="mt-8" data-id="element-496" data-mp="7ff1k3li4iz3">
            <ArrowLeftIcon className="h-4 w-4" data-id="element-497" data-mp="7fnetwljdqat" />
            Back to the shop
          </Button>
        </div>
      </main>;
  }
  const category = getCategory(product.category);
  const related = product.pairsWith.map(pair => products.find(item => item.slug === pair)).filter((item): item is NonNullable<typeof item> => Boolean(item));
  return <main data-id="element-498" data-mp="2i6nj71woatxa">
      <section className="bg-cream" data-id="element-499" data-mp="2i6x80m5zev1e">
        <div className="mx-auto max-w-shell px-6 pb-16 pt-8 md:px-8 md:pb-20 md:pt-12" data-id="element-500" data-mp="2i5vqbyugnaap">
          <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-2 text-sm" data-id="element-501" data-mp="2n7up0vf99j1b">
            <Link to="/shop" className="text-ink-muted transition-colors hover:text-ink" data-id="element-502" data-mp="233njcdo5ub3r">
              Shop
            </Link>
            <span className="text-ink-muted" data-id="element-503" data-mp="1z4xvts9erd6e">/</span>
            <Link to={`/categories/${product.category}`} className="text-ink-muted transition-colors hover:text-ink" data-id="element-504" data-mp="1uatp9a42175u">
              {category?.name}
            </Link>
            <span className="text-ink-muted" data-id="element-505" data-mp="1kegusa0oqpa1">/</span>
            <span className="text-ink" data-id="element-506" data-mp="1feuhk0kux918">{product.name}</span>
          </nav>

          <div className="mt-8 grid gap-10 lg:grid-cols-2 lg:gap-16" data-id="element-507" data-mp="1fgp1bbke6v3q">
            <div className="overflow-hidden rounded-brand bg-cream-deep" data-id="element-508" data-mp="1bdj1alnvi05p">
              <img src={productImage(product.slug, product.image)} alt={`${product.name}, ${product.form}`} className="aspect-square w-full object-cover" data-id="element-509" data-mp="16eoaoxjnz7b1" />
            </div>

            <div data-id="element-510" data-mp="30sp4v549r64s">
              <p className="text-[0.6875rem] uppercase tracking-label text-brick" data-id="element-511" data-mp="30q2znc4up2pq">
                {product.status}
              </p>
              <h1 className="mt-3 font-serif text-4xl font-semibold leading-[1.05] text-ink md:text-5xl" data-id="element-512" data-mp="3da017udbnoal">
                {product.name}
              </h1>
              <p className="mt-4 text-lg leading-relaxed text-ink-soft" data-id="element-513" data-mp="3i3ckrq3594wh">{product.tagline}</p>

              <dl className="rule mt-8 grid grid-cols-2 gap-6 pt-6 sm:grid-cols-3" data-id="element-514" data-mp="3n2fg2itbnwr9">
                <div data-id="element-515" data-mp="2pe5cw0hsv2yk">
                  <dt className="text-xs uppercase tracking-label text-ink-muted" data-id="element-516" data-mp="2pex8vcfb72pd">Form</dt>
                  <dd className="mt-2 text-sm text-ink" data-id="element-517" data-mp="2udrq49xmoj7d">{product.form}</dd>
                </div>
                <div data-id="element-518" data-mp="2z9gv4ssf2sb2">
                  <dt className="text-xs uppercase tracking-label text-ink-muted" data-id="element-519" data-mp="33cdfanxluwh4">Strength</dt>
                  <dd className="mt-2 text-sm text-ink" data-id="element-520" data-mp="33a0s9wczpvx7">{product.strength}</dd>
                </div>
                <div data-id="element-521" data-mp="im4joxizmsbz">
                  <dt className="text-xs uppercase tracking-label text-ink-muted" data-id="element-522" data-mp="illbi8j53ib5">Route</dt>
                  <dd className="mt-2 text-sm text-ink" data-id="element-523" data-mp="h4pcppemoekz">{product.route}</dd>
                </div>
              </dl>

              <div className="mt-8 rounded-brand border border-ink/10 bg-cream-light p-6" data-id="element-524" data-mp="c6nxxgo8j1fb">
                <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-525" data-mp="r3ofircppg02">
                  <div data-id="element-526" data-mp="m4brw4e0o4wf">
                    <p className="font-serif text-4xl font-semibold leading-none text-ink" data-id="element-527" data-mp="m3sixd6cgquj">
                      ${product.price}
                      <span className="ml-1 text-base font-normal text-ink-muted" data-id="element-528" data-mp="1028naxgqi59i">
                        {product.cadence}
                      </span>
                    </p>
                    <p className="mt-2 text-sm text-ink-muted" data-id="element-529" data-mp="19sl90t0ac4h8">{product.priceNote}</p>
                  </div>
                  <p className="text-sm text-ink-muted" data-id="element-530" data-mp="14vusk4kkj6va">
                    Plus $39/mo membership
                    <span className="block text-ink" data-id="element-531" data-mp="1iv476zt4lsx5">Same price for a year</span>
                  </p>
                </div>

                <div className="mt-6 flex flex-col gap-3 sm:flex-row" data-id="element-532" data-mp="1eqmshf1mi5t8">
                  <Button to={`/get-started?product=${product.slug}`} size="lg" className="flex-1" data-id="element-533" data-mp="1xibbimo3vsn2">
                    Ask about this
                    <ArrowRightIcon className="h-4 w-4" data-id="element-534" data-mp="22dqmn6uqbafs" />
                  </Button>
                  <Button to="/how-it-works" variant="secondary" size="lg" data-id="element-535" data-mp="1og1zfsd6vr7k">
                    How it works
                  </Button>
                </div>

                <ul className="mt-6 grid gap-3 sm:grid-cols-2" data-id="element-536" data-mp="1eqa8r987ni8d">
                  <li className="flex items-center gap-2 text-xs text-ink-muted" data-id="element-537" data-mp="1jh8pg9x42ic5">
                    <ShieldCheckIcon className="h-4 w-4 shrink-0 text-brick" strokeWidth={1.75} data-id="element-538" data-mp="1jjbkx9h193o9" />
                    Prescription only. Nothing charged until you say yes.
                  </li>
                  <li className="flex items-center gap-2 text-xs text-ink-muted" data-id="element-539" data-mp="19pcpetbgsi5a">
                    <TruckIcon className="h-4 w-4 shrink-0 text-brick" strokeWidth={1.75} data-id="element-540" data-mp="vs68l4aawtip" />
                    Free shipping in a plain box, cold-packed when it needs to be
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-cream-light" data-id="element-541" data-mp="2xnn9e0vx6qys">
        <div className="mx-auto max-w-shell px-6 py-16 md:px-8 md:py-20" data-id="element-542" data-mp="2tnmktnf3wze0">
          <div className="grid gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:gap-20" data-id="element-543" data-mp="2tlihp7p184es">
            <div data-id="element-544" data-mp="2oq36kniesmm2">
              <h2 className="font-serif text-2xl font-semibold text-ink md:text-3xl" data-id="element-545" data-mp="2omfbhjxbfs58">
                About {product.name}
              </h2>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-ink-soft" data-id="element-546" data-mp="3hcnybp2sr0q5">
                {text(product.description)}
              </p>
              <ul className="mt-8 flex flex-col gap-4" data-id="element-547" data-mp="3cc9n5y5wfanw">
                {product.highlights.map(highlight => <li key={highlight} className="flex items-start gap-3" data-id="element-548" data-mp="37hdssrd6wb7m">
                    <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-brick" strokeWidth={2.25} data-id="element-549" data-mp="210ijptj4x57i" />
                    <span className="text-base leading-relaxed text-ink-soft" data-id="element-550" data-mp="1vy9r4ya6c55x">{highlight}</span>
                  </li>)}
              </ul>
            </div>

            <div data-id="element-551" data-mp="1qw2676t4o06v">
              <h2 className="text-[0.6875rem] uppercase tracking-label text-ink-muted" data-id="element-552" data-mp="1qvin3mrqr5r9">
                The details
              </h2>
              <dl className="mt-5 rounded-brand border border-ink/10 bg-cream px-6" data-id="element-553" data-mp="1vs9184jzjra1">
                {product.facts.map(fact => <div key={fact.label} className="flex items-baseline justify-between gap-6 border-b border-ink/10 py-4 last:border-b-0" data-id="element-554" data-mp="24n6ytoy2vs7p">
                    <dt className="text-sm text-ink-muted" data-id="element-555" data-mp="2ekbm5s3nttq3">{fact.label}</dt>
                    <dd className="max-w-[60%] text-right text-sm text-ink" data-id="element-556" data-mp="2jjobcji07cvu">{fact.value}</dd>
                  </div>)}
                <div className="flex items-baseline justify-between gap-6 border-t border-ink/10 py-4" data-id="element-557" data-mp="k7lr371k2eh4">
                  <dt className="text-sm text-ink-muted" data-id="element-558" data-mp="p6g946rp7z06">Made by</dt>
                  <dd className="max-w-[60%] text-right text-sm text-ink" data-id="element-559" data-mp="2r2nque2cgk9x">{product.status}</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </section>

      {related.length > 0 && <section className="bg-cream" data-id="element-560" data-mp="30ryzjzlbjmxa">
          <div className="mx-auto max-w-shell px-6 py-16 md:px-8 md:py-20" data-id="element-561" data-mp="30uth9jcy6tn0">
            <div className="rule flex flex-wrap items-baseline justify-between gap-3 pt-6" data-id="element-562" data-mp="2vufgmmdoelz0">
              <h2 className="font-serif text-2xl font-semibold text-ink" data-id="element-563" data-mp="284uyu6t9durb">
                People usually run this alongside
              </h2>
              <Link to={`/categories/${product.category}`} className="text-sm text-brick transition-colors hover:text-ink" data-id="element-564" data-mp="2h519zm1hiinb">
                All {category?.name}
              </Link>
            </div>
            <ul className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-id="element-565" data-mp="3siv9osfzvdgv">
              {related.map(item => <ProductCard key={item.slug} product={item} data-id="element-566" data-mp="3oibdbr38wdvs" />)}
            </ul>
          </div>
        </section>}

      <CtaBand data-id="element-567" data-mp="4vt0gw13hk3b" />
    </main>;
}